import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useI18n } from "../i18n/I18nProvider";
import { Phone, Mail, Calendar, RefreshCw } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import AdminPages from "./AdminPages";
import { api, formatApiError } from "../lib/apiClient";
import { useAdminAuth } from "../admin/AdminAuthContext";
import { AdminLoginForm } from "../admin/AdminLogin";
import AdminShell from "../admin/AdminShell";

const STATUS_COLORS = {
  new: "#0A0A0A",
  contacted: "#0284C7",
  qualified: "#7C3AED",
  won: "#16A34A",
  lost: "#E11D48",
};

export default function AdminInbox() {
  const { t } = useI18n();
  const { admin } = useAdminAuth();
  const [demos, setDemos] = React.useState([]);
  const [calls, setCalls] = React.useState([]);
  const [contacts, setContacts] = React.useState([]);
  const [quotes, setQuotes] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [tab, setTab] = React.useState("demos");

  const refresh = React.useCallback(async () => {
    if (!admin) return;
    setLoading(true);
    try {
      const [d, c, n, q] = await Promise.all([
        api.get("/admin/demo-requests"),
        api.get("/admin/callback-requests"),
        api.get("/admin/contact-requests"),
        api.get("/admin/quotes"),
      ]);
      setDemos(d.data.items);
      setCalls(c.data.items);
      setContacts(n.data.items);
      setQuotes(q.data.items);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error(t("admin.invalid_credentials"));
      } else {
        toast.error(formatApiError(err, "Failed to load."));
      }
    } finally {
      setLoading(false);
    }
  }, [admin, t]);

  React.useEffect(() => {
    if (admin) refresh();
  }, [admin, refresh]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/demo-requests/${id}`, { status });
      setDemos((rows) => rows.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Marked ${status}`);
    } catch (err) {
      toast.error(formatApiError(err, "Update failed"));
    }
  };

  if (admin === null) {
    return (
      <section className="min-h-[80vh] grid place-items-center py-24">
        <div className="text-sm text-zinc-500">Loading...</div>
      </section>
    );
  }

  if (!admin) {
    return (
      <section className="min-h-[80vh] grid place-items-center py-24" data-testid="admin-login">
        <div className="w-full max-w-md px-4">
          <AdminLoginForm
            heading={t("admin.title")}
            subtitle={t("admin.inbox_help")}
          />
        </div>
      </section>
    );
  }

  return (
    <AdminShell>
      <div className="py-2" data-testid="admin-inbox">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
          <div>
            <h2 className="font-display text-2xl font-black tracking-tighter text-oj-ink">{t("admin.title")}</h2>
            <p className="text-sm text-zinc-500 mt-1">
              Demo inbox. To edit terms, privacy, about, pricing, or the configurator, open{" "}
              <Link to="/admin/pages" className="font-semibold text-oj-ink underline underline-offset-2">Pages</Link>
              {" "}in the left menu. Mailco keys:{" "}
              <Link to="/admin/settings" className="font-semibold text-oj-ink underline underline-offset-2">Settings</Link>.
            </p>
          </div>
          <button onClick={refresh} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-zinc-200 rounded-md hover:border-zinc-900" data-testid="admin-refresh">
            <RefreshCw className={`size-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-transparent gap-2 mb-6">
            <TabsTrigger data-testid="admin-tab-demos" value="demos" className="data-[state=active]:bg-oj-ink data-[state=active]:text-white rounded-md px-3 py-2">
              {t("admin.tab_demos")} <span className="ml-2 text-xs opacity-70">{demos.length}</span>
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-callbacks" value="callbacks" className="data-[state=active]:bg-oj-ink data-[state=active]:text-white rounded-md px-3 py-2">
              {t("admin.tab_callbacks")} <span className="ml-2 text-xs opacity-70">{calls.length}</span>
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-contacts" value="contacts" className="data-[state=active]:bg-oj-ink data-[state=active]:text-white rounded-md px-3 py-2">
              {t("admin.tab_contacts")} <span className="ml-2 text-xs opacity-70">{contacts.length}</span>
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-quotes" value="quotes" className="data-[state=active]:bg-oj-ink data-[state=active]:text-white rounded-md px-3 py-2">
              {t("admin.tab_quotes")} <span className="ml-2 text-xs opacity-70">{quotes.length}</span>
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-pages" value="pages" className="data-[state=active]:bg-oj-ink data-[state=active]:text-white rounded-md px-3 py-2">
              {t("admin.tab_pages")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="demos">
            {demos.length === 0 ? <Empty t={t} /> : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {demos.map((d) => (
                  <article key={d.id} data-testid={`admin-demo-${d.id}`} className="rounded-2xl border border-zinc-200 bg-white p-5">
                    <div className="flex items-center justify-between">
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{new Date(d.created_at).toLocaleString()}</div>
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded text-white" style={{ background: STATUS_COLORS[d.status] || "#0A0A0A" }}>
                        {d.status}
                      </span>
                    </div>
                    <div className="mt-3 font-display text-xl font-bold text-oj-ink">{d.full_name} · {d.business_name}</div>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-sm text-zinc-700">
                      <a href={`mailto:${d.email}`} className="inline-flex items-center gap-1 hover:underline"><Mail className="size-3.5" /> {d.email}</a>
                      <a href={`tel:${d.phone}`} className="inline-flex items-center gap-1 hover:underline"><Phone className="size-3.5" /> {d.phone}</a>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      <Chip>{d.business_type}</Chip>
                      {d.locations && <Chip>{d.locations}</Chip>}
                      {(d.interested_in || []).map((i) => <Chip key={i}>{i}</Chip>)}
                      {d.locale && <Chip>{d.locale.toUpperCase()}</Chip>}
                    </div>
                    {d.message && <p className="mt-3 text-sm text-zinc-600">{d.message}</p>}
                    <div className="mt-4 flex flex-wrap gap-2">
                      {["contacted", "qualified", "won", "lost"].map((s) => (
                        <button key={s} onClick={() => updateStatus(d.id, s)} data-testid={`admin-mark-${s}-${d.id}`}
                          className="text-xs font-semibold px-3 py-1.5 rounded-full border border-zinc-200 hover:border-zinc-900">
                          {t(`admin.mark_${s}`)}
                        </button>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="callbacks">
            {calls.length === 0 ? <Empty t={t} /> : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {calls.map((c) => (
                  <article key={c.id} data-testid={`admin-call-${c.id}`} className="rounded-2xl border border-zinc-200 bg-white p-5">
                    <div className="flex items-center justify-between">
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500"><Calendar className="inline size-3" /> {new Date(c.created_at).toLocaleString()}</div>
                      <Chip>{c.best_time || "asap"}</Chip>
                    </div>
                    <div className="mt-3 font-display text-xl font-bold text-oj-ink">{c.full_name}</div>
                    <a href={`tel:${c.phone}`} className="mt-1 text-zinc-700 inline-flex items-center gap-1 hover:underline"><Phone className="size-3.5" /> {c.phone}</a>
                    {c.topic && <p className="mt-3 text-sm text-zinc-600">{c.topic}</p>}
                    {c.locale && <div className="mt-3"><Chip>{c.locale.toUpperCase()}</Chip></div>}
                  </article>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="contacts">
            {contacts.length === 0 ? <Empty t={t} /> : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {contacts.map((n) => (
                  <article key={n.id} data-testid={`admin-contact-${n.id}`} className="rounded-2xl border border-zinc-200 bg-white p-5">
                    <div className="flex items-center justify-between">
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{new Date(n.created_at).toLocaleString()}</div>
                      {n.locale && <Chip>{n.locale.toUpperCase()}</Chip>}
                    </div>
                    <div className="mt-3 font-display text-xl font-bold text-oj-ink">{n.full_name}</div>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-sm text-zinc-700">
                      <a href={`mailto:${n.email}`} className="inline-flex items-center gap-1 hover:underline"><Mail className="size-3.5" /> {n.email}</a>
                      <a href={`tel:${n.phone}`} className="inline-flex items-center gap-1 hover:underline"><Phone className="size-3.5" /> {n.phone}</a>
                    </div>
                    {n.message && <p className="mt-3 text-sm text-zinc-600 whitespace-pre-wrap">{n.message}</p>}
                  </article>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="quotes">
            {quotes.length === 0 ? <Empty t={t} /> : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {quotes.map((q) => (
                  <article key={q.id} data-testid={`admin-quote-${q.id}`} className="rounded-2xl border border-zinc-200 bg-white p-5">
                    <div className="flex items-center justify-between">
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{new Date(q.created_at).toLocaleString()}</div>
                      <span className="font-display text-2xl font-black text-oj-ink">CHF {(q.hardware_total || 0).toFixed(0)}</span>
                    </div>
                    <div className="mt-3 font-display text-lg font-bold text-oj-ink">{q.full_name}{q.business_name ? ` · ${q.business_name}` : ""}</div>
                    <a href={`mailto:${q.email}`} className="text-sm text-zinc-700 inline-flex items-center gap-1 hover:underline"><Mail className="size-3.5" /> {q.email}</a>
                    <ul className="mt-3 text-sm text-zinc-700 space-y-1">
                      {(q.items || []).map((it) => (
                        <li key={it.sku} className="flex justify-between">
                          <span>{it.qty}× {it.name}</span>
                          <span className="font-mono">CHF {(it.qty * it.unit_price).toFixed(0)}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3 text-xs text-zinc-500">Software: CHF {(q.monthly_total || q.monthly_software || 0).toFixed(0)}/mo</div>
                  </article>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="pages">
            <AdminPages embedded />
          </TabsContent>
        </Tabs>
      </div>
    </AdminShell>
  );
}

function Chip({ children }) {
  return <span className="text-[11px] font-semibold uppercase tracking-wider bg-zinc-100 text-zinc-700 px-2 py-0.5 rounded-full">{children}</span>;
}
function Empty({ t }) {
  return <div className="rounded-2xl border border-dashed border-zinc-300 p-12 text-center text-zinc-500">{t("admin.empty")}</div>;
}
